/*
 * Copyright (c) 2022-2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */
/**
 * AUTO GENERATED
 */
/* eslint-disable camelcase */
import { AccelbyteSDK, ApiArgs, ApiUtils, Network } from '@accelbyte/sdk'
import { JwkSet } from '../generated-definitions/JwkSet.js'
import { TokenResponse } from '../generated-definitions/TokenResponse.js'
import { TokenVerifyResponse } from '../generated-definitions/TokenVerifyResponse.js'
import { OAuth20$ } from './endpoints/OAuth20$.js'

export function OAuth20Api(sdk: AccelbyteSDK, args?: ApiArgs) {
  const sdkAssembly = sdk.assembly()

  const namespace = args?.namespace ? args?.namespace : sdkAssembly.namespace
  const cache = args?.cache ? args?.cache : sdkAssembly.cache
  const requestConfig = ApiUtils.mergedConfigs(sdkAssembly.config, args)
  const isValidationEnabled = args?.isValidationEnabled !== false

  /**
   * <p>This endpoint serves public keys for verifying JWT access tokens generated by this service.</p> <p>When a client application wants to verify a JWT token, it needs to get the 'kid' value found in the JWT token header and use it to look up the corresponding public key from a set returned by this endpoint. The client application can then use that public key to verify the JWT.</p> <p>A client application might cache the keys so it doesn't need to do request every time it needs to verify a JWT token. If a client application caches the keys and a key with the same 'kid' cannot be found in the cache, it should then try to refresh the keys by making a request to this endpoint again.</p> <p>Please refer to the RFC for more information about JWK (JSON Web Key): https://tools.ietf.org/html/rfc7517</p>
   */
  async function getOauthJwks(): Promise<JwkSet> {
    const $ = new OAuth20$(Network.create(requestConfig), namespace, cache, isValidationEnabled)
    const resp = await $.getOauthJwks()
    if (resp.error) throw resp.error
    return resp.response.data
  }

  /**
   * OAuth2 access token generation
   */
  async function postOauthToken(data: {
    grant_type: 'authorization_code' | 'client_credentials' | 'password' | 'refresh_token'
    username?: string | null
    password?: string | null
    code?: string | null
    code_verifier?: string | null
    client_id?: string | null
    redirect_uri?: string | null
    refresh_token?: string | null
    extend_exp?: boolean | null
  }): Promise<TokenResponse> {
    const $ = new OAuth20$(Network.create(requestConfig), namespace, cache, isValidationEnabled)
    const resp = await $.postOauthToken(data)
    if (resp.error) throw resp.error
    return resp.response.data
  }

  /**
   * This endpoint is used to remove **access_token**, **refresh_token** from cookie.
   */
  async function createOauthLogout(): Promise<unknown> {
    const $ = new OAuth20$(Network.create(requestConfig), namespace, cache, isValidationEnabled)
    const resp = await $.createOauthLogout()
    if (resp.error) throw resp.error
    return resp.response.data
  }

  /**
   * This endpoint requires all requests to have Authorization header set with Basic access authentication constructed from client id and client secret.
   */
  async function postOauthVerify(data: { token: string | null }): Promise<TokenVerifyResponse> {
    const $ = new OAuth20$(Network.create(requestConfig), namespace, cache, isValidationEnabled)
    const resp = await $.postOauthVerify(data)
    if (resp.error) throw resp.error
    return resp.response.data
  }

  /**
   * OAuth2 authorize request
   */
  async function getOauthAuthorize(queryParams: {
    response_type: 'code'
    client_id: string | null
    redirect_uri?: string | null
    state?: string | null
    code_challenge?: string | null
    code_challenge_method?: 'S256' | 'plain'
    target_auth_page?: string | null
  }): Promise<unknown> {
    const $ = new OAuth20$(Network.create(requestConfig), namespace, cache, isValidationEnabled)
    const resp = await $.getOauthAuthorize(queryParams)
    if (resp.error) throw resp.error
    return resp.response.data
  }

  /**
   * This endpoint is being used to authenticate a user account. It validates user's email and password. Redirect URI and Client ID must be specified as a pair and only used to redirect to the specified redirect URI in case the requestId is no longer valid.
   */
  async function postOauthAuthenticate(data: {
    username: string | null
    password: string | null
    request_id: string | null
    redirect_uri?: string | null
    client_id?: string | null
  }): Promise<unknown> {
    const $ = new OAuth20$(Network.create(requestConfig), namespace, cache, isValidationEnabled)
    const resp = await $.postOauthAuthenticate(data)
    if (resp.error) throw resp.error
    return resp.response.data
  }

  return {
    getOauthJwks,
    postOauthToken,
    createOauthLogout,
    postOauthVerify,
    getOauthAuthorize,
    postOauthAuthenticate
  }
}
